import useTranslation from '@/hooks/useTranslation'
import {
  decodeStationError,
  getStationErrorDetailText,
} from '@/utils/stationErrorDetail'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'

// Shared bits of the Error Summary tables. The table body stays white in both
// themes, so colours are fixed here rather than taken from the palette.

export const ERROR_RED = '#B42318'
export const MUTED_TEXT = '#5C5C5C'
export const SUCCESS_GREEN = '#067647'

/** `muted`: the error was recorded earlier but is not happening now. */
export const ErrorCodeChip = ({
  code,
  muted,
}: {
  code: number | string
  muted?: boolean
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 1,
      py: '2px',
      borderRadius: '4px',
      bgcolor: muted ? '#EEF1F3' : '#FDECEA',
      color: muted ? MUTED_TEXT : ERROR_RED,
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    {code}
  </Box>
)

export const ReasonText = ({
  text,
  muted,
}: {
  text?: string
  muted?: boolean
}) => (
  <Typography
    sx={{
      fontSize: '13px',
      lineHeight: 1.5,
      color: muted ? MUTED_TEXT : '#1B1B1B',
    }}
  >
    {text?.trim() ? text : '-'}
  </Typography>
)

// Diagnose returns actions as text lines that may already carry their own
// list markers ("- ", "• ", "1. "); the list below adds its own.
const LIST_MARKER = /^(?:[-–•*]|\d+[.)])\s+/

export const normalizeSteps = (actions: string[] = []) =>
  actions
    .map((step) => step.trim().replace(LIST_MARKER, '').trim())
    .filter(Boolean)

export const SuggestedActionList = ({
  actions,
  muted,
}: {
  actions: string[]
  muted?: boolean
}) => {
  const steps = normalizeSteps(actions)
  if (steps.length === 0) {
    return <ReasonText muted={muted} />
  }
  if (steps.length === 1) {
    return <ReasonText text={steps[0]} muted={muted} />
  }
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        fontSize: '13px',
        lineHeight: 1.5,
        color: muted ? MUTED_TEXT : '#1B1B1B',
      }}
    >
      {steps.map((step, idx) => (
        <li key={idx}>{step}</li>
      ))}
    </Box>
  )
}

export const primaryActionSx = {
  height: 36,
  width: '100%',
  px: 1.75,
  whiteSpace: 'nowrap',
  bgcolor: '#004744',
  color: '#FFFFFF',
  fontSize: '13px',
  fontWeight: 600,
  '&:hover': { bgcolor: '#065F46' },
  '&.Mui-disabled': { bgcolor: '#004744', color: '#FFFFFF', opacity: 0.6 },
}

export const secondaryActionSx = {
  height: 36,
  width: '100%',
  px: 1.75,
  whiteSpace: 'nowrap',
  bgcolor: '#E6EDED',
  color: '#004744',
  fontSize: '13px',
  fontWeight: 600,
  '&:hover': { bgcolor: '#D5E1E0' },
  '&.Mui-disabled': { bgcolor: '#E6EDED', color: '#004744', opacity: 0.6 },
}

/** Diagnose composite codes (30009) read as code-bit (3000-9). */
export const formatErrorCode = (code: number) => {
  const decoded = decodeStationError(code)
  return decoded && decoded.bit != null
    ? `${decoded.code}-${decoded.bit}`
    : String(code)
}

export interface DiagnosisResult {
  passed: boolean
  code?: number
  message?: string
  reason?: string
  actions?: string[]
  /** When the diagnostic finished (ms). */
  at: number
}

const Timestamp = ({ children }: { children: string }) => (
  <Typography
    sx={{ fontSize: '12px', color: MUTED_TEXT, whiteSpace: 'nowrap' }}
  >
    {children}
  </Typography>
)

/**
 * Error column of the main row: the error the controller recorded. Greyed
 * once a diagnostic shows it is no longer happening.
 */
export const ErrorStatusCell = ({
  code,
  message,
  recordedAt,
  recordedAtLoading,
  past,
}: {
  code: number
  message: string
  recordedAt?: string
  /** The log holding recordedAt is still being fetched. */
  recordedAtLoading?: boolean
  past?: boolean
}) => {
  const { t } = useTranslation()
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <ErrorCodeChip code={code} muted={past} />
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: 1.45,
          color: past ? MUTED_TEXT : ERROR_RED,
        }}
      >
        {message || '-'}
      </Typography>
      {recordedAtLoading ? (
        <Skeleton variant="text" width={110} sx={{ fontSize: '12px' }} />
      ) : (
        <Timestamp>
          {recordedAt
            ? t('errorSummaryRecordedAt', {
                time: dayjs(recordedAt).format('D MMM, HH:mm'),
              })
            : t('errorSummaryRecorded')}
        </Timestamp>
      )}
    </Stack>
  )
}

const DIAGNOSTIC_TINT = {
  running: { bg: '#F3F6F5', border: '#DCE3E0' },
  passed: { bg: '#EEF8F2', border: '#C6E7D3' },
  failed: { bg: '#FDF1EF', border: '#F3CFC9' },
}

const CONNECTOR = '#C8D0D4'

/** Main-row cells lose their bottom border so the expansion reads as part of the row. */
export const groupedCellSx = { borderBottom: 0 }

/** True when Diagnose returned the same guidance the row already shows. */
const sameGuidance = (
  diagnosis: DiagnosisResult | undefined,
  loggedReason?: string,
  loggedActions?: string[],
) =>
  (diagnosis?.reason ?? '').trim() === (loggedReason ?? '').trim() &&
  normalizeSteps(diagnosis?.actions).join('\n') ===
    normalizeSteps(loggedActions).join('\n')

const DetailLabel = ({ children }: { children: string }) => (
  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: MUTED_TEXT }}>
    {children}
  </Typography>
)

/**
 * Expansion under a station or flow valve row: one status line for the latest
 * Diagnose (or its progress), plus only what it adds to the row above.
 */
export const DiagnosticRow = ({
  running,
  recordedCode,
  diagnosis,
  loggedReason,
  loggedActions,
  rowBg,
}: {
  running: boolean
  recordedCode: number
  diagnosis?: DiagnosisResult
  /** Guidance already shown in the row, so the panel doesn't repeat it. */
  loggedReason?: string
  loggedActions?: string[]
  /** Background of the parent row, so the two read as one block. */
  rowBg: string
}) => {
  const { t } = useTranslation()
  if (!running && !diagnosis) return null

  const state = running ? 'running' : diagnosis?.passed ? 'passed' : 'failed'
  // Fill gaps in the Diagnose result from the catalogue for its code
  const catalogue =
    diagnosis?.code != null
      ? getStationErrorDetailText(t, diagnosis.code)
      : null
  const effective: DiagnosisResult | undefined = diagnosis && {
    ...diagnosis,
    message: diagnosis.message?.trim() || catalogue?.title,
    reason: diagnosis.reason?.trim() || catalogue?.reason,
    actions: normalizeSteps(diagnosis.actions).length
      ? diagnosis.actions
      : catalogue?.actions,
  }
  // Diagnose only earns its own code when it says more than the recorded one
  const sameError = diagnosis?.code == null || diagnosis.code === recordedCode
  const title =
    state === 'running'
      ? t('errorSummaryDiagnosticRunning')
      : state === 'passed'
        ? t('errorSummaryNoErrorNow')
        : sameError
          ? t('errorSummaryStillFailing')
          : `${formatErrorCode(diagnosis?.code as number)} ${
              effective?.message ?? ''
            }`.trim()
  const color =
    state === 'running'
      ? '#004744'
      : state === 'passed'
        ? SUCCESS_GREEN
        : ERROR_RED
  const tint = DIAGNOSTIC_TINT[state]
  const repeatsRow =
    state === 'failed' && sameGuidance(effective, loggedReason, loggedActions)

  return (
    <TableRow sx={{ bgcolor: rowBg }}>
      <TableCell colSpan={5} sx={{ pt: 0, pb: 2, px: '20px' }}>
        <Stack direction="row" alignItems="flex-start">
          {/* └ connector from the row above into the panel */}
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 18,
              height: 26,
              ml: '4px',
              mt: '-8px',
              borderLeft: `2px solid ${CONNECTOR}`,
              borderBottom: `2px solid ${CONNECTOR}`,
              borderBottomLeftRadius: '6px',
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{
              flex: 1,
              minWidth: 0,
              px: 2,
              py: 1.25,
              borderRadius: '8px',
              bgcolor: tint.bg,
              border: `1px solid ${tint.border}`,
            }}
          >
            {state === 'running' ? (
              <CircularProgress
                size={16}
                thickness={5}
                sx={{ color, mt: '2px', flexShrink: 0 }}
              />
            ) : state === 'passed' ? (
              <CheckCircleOutlineIcon
                sx={{ fontSize: 18, color, mt: '1px', flexShrink: 0 }}
              />
            ) : (
              <HighlightOffIcon
                sx={{ fontSize: 18, color, mt: '1px', flexShrink: 0 }}
              />
            )}
            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color }}>
                {title}
                {diagnosis && state !== 'running' && (
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: MUTED_TEXT, ml: 1 }}
                  >
                    {'· '}
                    {t('errorSummaryDiagnosedAt', {
                      time: dayjs(diagnosis.at).format('HH:mm'),
                    })}
                  </Box>
                )}
              </Typography>

              {state === 'running' && (
                <Box role="status" aria-live="polite">
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width="45%"
                    height={18}
                  />
                </Box>
              )}
              {state === 'passed' && (
                <ReasonText text={t('errorSummaryDiagnosticPassedDetail')} />
              )}
              {repeatsRow && (
                <ReasonText text={t('errorSummaryDiagnosticConfirms')} />
              )}
              {state === 'failed' && !repeatsRow && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '130px minmax(0, 1fr)',
                    },
                    columnGap: 2,
                    rowGap: 0.75,
                  }}
                >
                  <DetailLabel>{t('diagnosticReason')}</DetailLabel>
                  <ReasonText text={effective?.reason} />
                  <DetailLabel>{t('suggestedAction')}</DetailLabel>
                  <SuggestedActionList actions={effective?.actions ?? []} />
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  )
}
